<template>
  <div class="product-card my-2">
    <div class="card bg-dark text-white">
      <!-- Modificar el badge de descuento para que solo aparezca si hay descuento mayor a 0 y el usuario está autenticado -->
      <div v-if="isAuthenticated && selectedBlister && selectedBlister.discount > 0" class="discount-badge-wrapper">
        <span class="discount-badge p-2">
          -{{ formatDiscount(selectedBlister.discount) }}% OFF
        </span>
      </div>
      <a :href="`/producto/${product.id}`" class="card-img-wrapper">
        <div class="image-container">
          <img 
            :src="product.image" 
            :alt="product.name" 
            class="card-img"
            :class="{ 'loading': !imageLoaded }"
            @load="handleImageLoad"
          >
          <!-- Overlay con efecto hover -->
          <div class="img-overlay">
          </div>
        </div>
      </a>
      <div class="card-body p-3">
        <div class="d-flex justify-content-between">
          <div style="min-height: 80px;" class="d-flex flex-column justify-content-start">
            <a :href="`/producto/${product.id}`" class="text-decoration-none">
              <h6 class="brand mb-1 fw-light" v-if="product.brand">
                {{ product.brand }}
              </h6>
              <h5 class="product-name mb-2">
                {{ product.name }}
              </h5>
            </a>
          </div>
          <button @click="showModal" class="bg-transparent border-0 text-white d-flex align-items-center mb-2 eye-icon">
            <i class="far fa-eye"></i>
          </button>
        </div>
        <div class="d-flex justify-content-between align-items-center text-white">
          <div v-if="isAuthenticated" class="d-flex flex-column gap-2">
            <div class="d-flex align-items-center gap-2">
              <select 
                v-model="selectedBlister" 
                class="form-select form-select-sm blister-select fs-7"
                :class="{ 'disabled': !product.blisters || !product.blisters.length }"
              >
                <option value="" disabled selected>Cant.</option>
                <option 
                  v-for="blister in product.blisters || []" 
                  :key="blister.id" 
                  :value="blister"
                  :disabled="isBlisterDisabled(blister)"
                >
                  x{{ blister.seed_count }}
                  <span v-if="getBlisterStatusText(blister)"> - {{ getBlisterStatusText(blister) }}</span>
                </option>
              </select>
              <div class="price fw-light" v-if="selectedBlister && shouldShowPrice(selectedBlister)">
                <div class="d-flex align-items-center gap-2">
                  <div class="d-flex flex-column">
                    <span v-if="selectedBlister.discount > 0" class="original-price text-muted text-decoration-line-through fs-7">
                      ${{ formatPrice(selectedBlister.price) }}
                    </span>
                    <span class="fs-6">
                      ${{ formatPrice(selectedBlister.final_price || selectedBlister.price) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="login-required-text">
            <small class="text-muted">Inicia sesión para ver precio</small>
          </div>
          <button 
            v-if="isAuthenticated()"
            @click.stop="handleAddToCart" 
            class="btn cart-btn p-0"
            :disabled="!selectedBlister || !shouldShowPrice(selectedBlister) || selectedBlister.stock <= 0"
          >
            <i class="fas fa-shopping-cart fs-6 p-0"></i>
          </button>
        </div>
      </div>
    </div>

  </div>

  <!-- Custom Modal -->
  <Transition name="modal">
    <div v-if="isModalOpen" class="custom-modal mt-5">
      <div class="modal-backdrop" @click="closeModal"></div>
      <div class="modal-dialog">
        <div class="modal-content text-white p-2">
          <div class="modal-header border-bottom border-secondary">
            <h5 class="modal-title fw-bold fs-4">{{ product.name }}</h5>
            <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-5 d-flex align-items-start p-3 justify-content-center">
                <img :src="product.image" :alt="product.name" class="img-fluid product-modal-img h-100">
              </div>
              <div class="col-md-7 p-3">
                <div class="tag mb-2">{{ product.brand }}</div>
                <h1 class="mb-2">{{ product.name }}</h1>
                <!-- Specifics Grid -->
                <div class="specifics d-flex flex-wrap align-items-start pb-0 mt-3">
                  <div 
                    class="specific d-flex flex-column align-items-center justify-content-center"
                    :class="{ 'specific-disabled': !product.flowering_time }"
                  >
                    <div class="specific__img-wrapper">
                      <img src="@/assets/icons/cannabis.svg" alt="icono de cannabis" class="img-fluid" />
                    </div>
                    <h4 class="fw-semibold fs-7 mb-0">Floración</h4>
                    <p v-if="product.flowering_time" class="fs-7">{{ product.flowering_time }} Días</p>
                    <p v-else class="fs-7 text-muted">N/D</p>
                  </div>
                  <div 
                    class="specific d-flex flex-column align-items-center justify-content-center"
                    :class="{ 'specific-disabled': !product.thc }"
                  >
                    <div class="specific__img-wrapper">
                      <img src="@/assets/icons/bolt.svg" alt="icono de rayo" class="img-fluid" />
                    </div>
                    <h4 class="fw-semibold fs-7 mb-0">THC</h4>
                    <p v-if="product.thc" class="fs-7">{{ product.thc }}</p>
                    <p v-else class="fs-7 text-muted">N/D</p>
                  </div>
                  <div 
                    class="specific d-flex flex-column align-items-center justify-content-center"
                    :class="{ 'specific-disabled': !product.cbd }"
                  >
                    <div class="specific__img-wrapper">
                      <img src="@/assets/icons/medical-drug.svg" alt="icono de pastilla" class="img-fluid" />
                    </div>
                    <h4 class="fw-semibold fs-7 mb-0">CBD</h4>
                    <p v-if="product.cbd" class="fs-7">{{ product.cbd }}</p>
                    <p v-else class="fs-7 text-muted">N/D</p>
                  </div>
                  <div 
                    class="specific d-flex flex-column align-items-center justify-content-center"
                    :class="{ 'specific-disabled': !product.flavors }"
                  >
                    <div class="specific__img-wrapper mb-2">
                      <img src="@/assets/icons/ice-cream.svg" alt="icono de helado" class="img-fluid" />
                    </div>
                    <h4 class="fw-semibold fs-7 mb-0">Sabor</h4>
                    <div v-if="product.flavors" class="flavors-list">
                      <span class="text-white fs-7 fw-light">
                        {{ product.flavors.split(',')[0] }}
                      </span>
                    </div>
                    <p v-else class="fs-7 text-muted">N/D</p>
                  </div>
                </div>

                <div class="description-wrapper mt-4">
                  <p class="fs-6">
                    {{ truncateText(product.description || '', 200) }}
                    <span v-if="product.description && product.description.length > 200" class="text-muted">...</span>
                  </p>
                </div>
                
                <!-- Ver más button -->
                <div class="mt-4">
                  <a :href="`/producto/${product.id}`" class="btn btn-primary w-100">
                    <i class="fas fa-info-circle me-2"></i>
                    Ver más información
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, defineProps, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { truncateText } from "@/helpers/text";
import { formatPrice } from "@/helpers/product";
import { useStore } from 'vuex'
import { isAuthenticated } from "@/services/auth";
import { 
  validateBlisterForCart, 
  getFirstAvailableBlister, 
  isBlisterDisabled, 
  getBlisterStatusText,
  calculateDiscountedPrice,
  shouldShowPrice
} from "@/helpers/blisterUtils";

// Define prop con validación
const props = defineProps({
  product: {
    type: Object,
    required: true,
    default: () => ({
      id: null,
      brand_id: '',
      brand: '',
      name: '',
      image: '',
      image_2: null,
      image_3: null,
      description: '',
      blisters: [], // Ensure blisters is always initialized as an array
      flowering_time: 0,
      thc: '',
      cbd: '',
      flavors: '',
      type: '',
      is_national: false
    })
  }
})

const isModalOpen = ref(false)
const store = useStore()
const toast = useToast()
const imageLoaded = ref(false)

const selectedBlister = ref(null)

// Watch for product changes to set initial blister with safeguards against undefined
watch(() => props.product, (newProduct) => {
  // Make sure newProduct and blisters exist and blisters is an array
  if (newProduct && newProduct.blisters && Array.isArray(newProduct.blisters) && newProduct.blisters.length) {
    selectedBlister.value = getFirstAvailableBlister(newProduct.blisters);
  } else {
    // Reset selected blister if no valid blisters available
    selectedBlister.value = null
  }
}, { immediate: true })

const showModal = () => {
  isModalOpen.value = true
  document.body.style.overflow = 'hidden'
}

const closeModal = () => {
  isModalOpen.value = false
  document.body.style.overflow = 'auto'
}
calculateDiscountedPrice
const handleAddToCart = () => {
  if (!isAuthenticated()) {
    toast.error('Debes iniciar sesión para agregar productos al carrito');
    return;
  }

  // Verificar que haya un blister seleccionado y que tenga stock
  if (!selectedBlister.value) {
    toast.error('No se ha seleccionado una presentación');
    return;
  }

  if (!shouldShowPrice(selectedBlister.value)) {
    toast.error('Esta presentación no tiene precio asignado');
    return;
  }

  // Verificar stock específicamente para el carrito
  if (selectedBlister.value.stock <= 0) {
    toast.error('No hay stock disponible para esta presentación');
    return;
  }

  // Usar el helper para validar el blister
  const validation = validateBlisterForCart(selectedBlister.value);
  if (!validation.isValid) {
    toast.error(validation.reason);
    return;
  }

  const finalPrice = selectedBlister.value.final_price || selectedBlister.value.price;

  try {
    store.dispatch('addToCart', {
      id: props.product.id,
      blister_id: selectedBlister.value.id,
      name: props.product.name,
      price: Number(finalPrice),
      original_price: Number(selectedBlister.value.price),
      discount: parseFloat(selectedBlister.value.discount),
      brand: props.product.brand,
      image: props.product.image,
      quantity: 1
    });
    
    toast.success('Producto añadido al carrito');
  } catch (error) {
    toast.error(error.message || 'Error al agregar el producto al carrito');
  }
}

const handleImageLoad = () => {
  imageLoaded.value = true
}
const formatDiscount = (discount) => {
  // Redondear al número entero más cercano sin agregar ceros al inicio
  const roundedDiscount = Math.round(discount);
  return roundedDiscount.toString();
}
</script>

<style scoped>
.product-card {
  border-radius: 16px;
}

.card {
  border-radius: 16px;
  border: none;
  background: linear-gradient(to bottom, rgba(40, 40, 40, 0.8), rgb(20, 20, 20));
  transition: transform 0.2s ease;
  height: 100%;
  position: relative; /* Asegurar que el badge se posicione correctamente */
}

.card-img-wrapper {
  display: block;
  padding: 1rem;
  padding-bottom: 0;
  text-decoration: none;
}

.image-container {
  position: relative;
  width: 100%;
  padding-top: 100%; /* Mantiene aspect ratio 1:1 */
  overflow: hidden;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.05);
}

.card-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  transition: transform 0.3s ease;
  opacity: 1;
}

.card-img.loading {
  opacity: 0;
}

.img-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.img-overlay i {
  color: white;
  font-size: 1.5rem;
  transform: translateY(20px);
  transition: transform 0.3s ease;
}

.card:hover .img-overlay {
  opacity: 1;
}

.card:hover .img-overlay i {
  transform: translateY(0);
}

.card:hover .card-img {
  transform: scale(1.05);
}

/* Modal styles update */
.product-modal-img {
  width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: contain;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .image-container {
    padding-top: 100%; /* Mantiene el aspect ratio en móvil */
  }
  
  .card {
    margin-bottom: 1rem;
  }
}

.brand {
  font-size: 0.75rem;
  color: #808080;
}

.product-name {
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}

.price {
  font-size: 1.1rem;
  font-weight: 600;
}

.cart-btn {
  width: 35px;
  height: 35px;
  border-radius: 7px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.cart-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.fa-shopping-cart {
  font-size: 1rem;
}

.eye-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
}

.card:hover .eye-icon {
  opacity: 1;
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
.modal-dialog{
  max-height: 350px;
  width: 90%;
  margin: 1.75rem auto;
  position: relative;
  z-index: 9999;
}

.modal-content {
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  background-color: var(--black-color-3);
  border-radius: 5px;
}

.tag {
  background-color: var(--dark-color);
  color: var(--white-color-2);
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 500;
  display: inline-block;
}

.modal-price {
  font-size: 2rem;
  font-weight: 600;
  color: #fff;
}


.cart-btn:hover i {
  animation: bounce 0.5s;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

@media (max-width: 768px) {
  .card-img-top {
    height: 200px;
  }
}

/* Custom Modal Styles */
.custom-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(5px);
  z-index: 9998;
}

.modal-dialog {
  position: relative;
  width: 90%;
  max-width: 800px;
  z-index: 9999;
  margin: 1.75rem auto;
}

/* Modal Transition Animations */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.modal-enter-to,
.modal-leave-from {
  opacity: 1;
  transform: scale(1);
}

/* Add these new styles */
.specifics {
  gap: 1rem;
  margin: 1rem 0;
}

.specific {
  flex: 0 0 auto;
  min-width: 80px;
  padding: 0.5rem;
  border-radius: 8px;
}

.specific__img-wrapper {
  width: 24px;
  height: 24px;
  margin-bottom: 0.5rem;
}

.specific__img-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.specific h4 {
  font-size: 0.75rem;
  text-align: center;
}

.specific p {
  font-size: 0.7rem;
  text-align: center;
  margin: 0;
}

.specific-disabled {
  opacity: 0.5;
  filter: grayscale(1);
}

.flavors-list {
  text-align: center;
}

/* Adjust modal max-height */
.modal-dialog {
  max-height: 600px; /* Increased to accommodate new content */
}

.form-select {
  background-color: var(--black-color-3) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-select:hover:not(.disabled) {
  border-color: var(--primary-color);
}

.form-select:focus {
  border-color: var(--primary-color);
  box-shadow: none;
}

.form-select.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-select option {
  background-color: var(--black-color-3);
  color: var(--white-color);
  font-size: 0.9rem;
}

.form-select option:disabled {
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  font-size: 0.9rem;
}

.cart-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.blister-select {
  width: auto;
  min-width: 40px;
  height: 35px;
  padding: 0.25rem 1.5rem 0.25rem 0.5rem;
  font-size: 0.85rem;
  background-position: right 0.25rem center;
  background-size: 16px 12px;
}

.form-select {
  background-color: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white-color);
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-select:hover:not(.disabled) {
  border-color: var(--primary-color);
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.form-select:focus {
  border-color: var(--primary-color);
  box-shadow: none;
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.form-select.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-select option {
  background-color: var(--black-color-3);
  color: var(--white-color);
  padding: 0.5rem;
}

.form-select option:disabled {
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
}

.stock-text {
  font-size: 0.7em;
  opacity: 0.6;
  font-style: italic;
}

.discount-badge {
  background: var(--primary-color);
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  animation: pulse 2s infinite;
}

.discount-badge-wrapper {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
}

.original-price {
  font-size: 0.85rem;
  opacity: 0.7;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

/* Si el precio tiene descuento, mostrar en verde */
.price .text-success {
  color: #28a745 !important;
  font-weight: 600;
}

/* Ajustes responsive */
@media (max-width: 768px) {
  .discount-badge {
    padding: 0.15rem 0.4rem;
    font-size: 0.7rem;
  }
}

/* Media query para pantallas muy pequeñas */
@media (max-width: 450px) {
  .blister-select {
    min-width: 25px;
    width: 50px;
    padding: 0.25rem;
    font-size: 0.8rem;
    background-position: right 0.1rem center;
    background-size: 12px 8px;
  }

  .price {
    font-size: 0.8rem;
  }

  .cart-btn {
    width: 28px;
    height: 28px;
    min-width: 28px; /* Asegurar ancho mínimo */
  }

  .card-body {
    padding: 0.5rem !important;
  }

  .fa-shopping-cart {
    font-size: 0.8rem;
  }

  .d-flex.justify-content-between.align-items-center {
    gap: 0.25rem;
    flex-wrap: nowrap; /* Prevenir wrap */
  }

  /* Ajustes adicionales */
  .product-name {
    font-size: 0.9rem;
  }

  .brand {
    font-size: 0.7rem;
  }

  .original-price {
    font-size: 0.7rem;
  }

  /* Contenedor del precio */
  .price.fw-light {
    min-width: 0;
    flex-shrink: 1;
  }

  /* Asegurar que el select no crezca demasiado */
  .d-flex.align-items-center.gap-2 {
    gap: 0.25rem !important;
    flex-wrap: nowrap;
  }
}

/* Media query adicional para pantallas extremadamente pequeñas */
@media (max-width: 360px) {
  .blister-select {
    width: 45px;
    padding-right: 0.15rem;
  }

  .card-body {
    padding: 0.35rem !important;
  }
}

/* Media query adicional para pantallas extremadamente pequeñas */
@media (max-width: 450px) {
  .blister-select {
    min-width: 30px; /* Reducir el ancho mínimo */
    padding-right: 1rem; /* Reducir el padding derecho */
    padding-left: 0.25rem; /* Reducir el padding izquierdo */
  }

  .price {
    font-size: 0.9rem; /* Reducir tamaño de fuente del precio */
  }

  .cart-btn {
    width: 30px; /* Hacer el botón más pequeño */
    height: 30px;
  }

  .card-body {
    padding: 0.75rem !important; /* Reducir el padding general */
  }

  .fa-shopping-cart {
    font-size: 0.85rem; /* Reducir tamaño del ícono */
  }

  /* Asegurar que el contenedor de precio y botones se ajuste correctamente */
  .d-flex.justify-content-between.align-items-center {
    gap: 0.5rem; /* Reducir el espacio entre elementos */
  }
}

.login-required-text {
  font-size: 0.8rem;
  color: var(--text-muted);
  font-style: italic;
}
</style>